import React, { useMemo, useState } from "react";
import styled from "styled-components/macro";
import { SearchRegular } from "@fluentui/react-icons";

import {
    Checkbox,
    CountPill,
    FieldBox,
    InitialsAvatar,
    PopoverEmpty,
    PopoverItem,
    PopoverPanel,
    PopoverSectionLabel,
    UnstyledButton,
} from "./index.style";
import { getInitials } from "./format";

const MAX_PEOPLE_WITHOUT_QUERY = 8;
const MAX_PEOPLE_WITH_QUERY = 20;

const includes = (text = "", query) => text.toLowerCase().includes(query);

/**
 * Recherche côté front dans les options du filtre : chaque élément choisi devient un token (valeur ud)
 * @param {{ groups?: {id: string, name: string, count: number}[], users?: {id: string, name: string, group_name: string}[], value: string[], onToggle: function, onClear: function, placeholder: string }} props
 */
const SearchTokens = ({
    groups = [],
    users = [],
    value = [],
    onToggle,
    onClear,
    placeholder,
}) => {
    const [open, setOpen] = useState(false);
    const [search, setSearch] = useState("");
    const query = search.trim().toLowerCase();

    const options = useMemo(() => {
        const visibleGroups = groups.filter(
            (group) => !query || includes(group.name, query)
        );
        const visibleUsers = users
            .filter((user) => !query || includes(user.name, query))
            .slice(0, query ? MAX_PEOPLE_WITH_QUERY : MAX_PEOPLE_WITHOUT_QUERY);
        return { groups: visibleGroups, users: visibleUsers };
    }, [groups, users, query]);

    const tokens = value.map((id) => ({
        id,
        isGroup: id.startsWith("d-"),
        label:
            groups.find((g) => g.id === id)?.name ??
            users.find((u) => u.id === id)?.name ??
            id,
    }));

    const toggle = (e, id) => {
        e.preventDefault();
        onToggle(id);
        setSearch("");
    };

    const isEmpty = !options.groups.length && !options.users.length;

    return (
        <Wrapper>
            <FieldBox $focused={open}>
                <Tokens>
                    {tokens.map((token) => (
                        <Token key={token.id} $isGroup={token.isGroup}>
                            {token.label}
                            <TokenRemove
                                aria-label={`Retirer ${token.label}`}
                                onMouseDown={(e) => toggle(e, token.id)}
                            >
                                ×
                            </TokenRemove>
                        </Token>
                    ))}
                    <Input
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        onFocus={() => setOpen(true)}
                        onBlur={() => setOpen(false)}
                        placeholder={tokens.length ? "Ajouter…" : placeholder}
                    />
                </Tokens>
                {tokens.length > 0 && (
                    <ClearButton
                        title="Tout effacer"
                        onMouseDown={(e) => {
                            e.preventDefault();
                            onClear();
                            setSearch("");
                        }}
                    >
                        ×
                    </ClearButton>
                )}
                <SearchRegular fontSize={14} color="#616161" />
            </FieldBox>
            {open && (
                <PopoverPanel>
                    {options.groups.length > 0 && (
                        <>
                            <PopoverSectionLabel>Départements</PopoverSectionLabel>
                            {options.groups.map((group) => (
                                <PopoverItem
                                    key={group.id}
                                    onMouseDown={(e) => toggle(e, group.id)}
                                >
                                    <Checkbox checked={value.includes(group.id)} />
                                    <GroupName>{group.name}</GroupName>
                                    <CountPill>{group.count}</CountPill>
                                </PopoverItem>
                            ))}
                        </>
                    )}
                    {options.users.length > 0 && (
                        <>
                            <PopoverSectionLabel
                                $separated={options.groups.length > 0}
                            >
                                Collaborateurs
                            </PopoverSectionLabel>
                            {options.users.map((user) => (
                                <PopoverItem
                                    key={user.id}
                                    onMouseDown={(e) => toggle(e, user.id)}
                                >
                                    <Checkbox checked={value.includes(user.id)} />
                                    <InitialsAvatar
                                        size={26}
                                        initials={getInitials(
                                            ...user.name.split(" ")
                                        )}
                                    />
                                    <UserInfos>
                                        <strong>{user.name}</strong>
                                        <small>{user.group_name}</small>
                                    </UserInfos>
                                </PopoverItem>
                            ))}
                        </>
                    )}
                    {isEmpty && <PopoverEmpty>Aucun résultat</PopoverEmpty>}
                </PopoverPanel>
            )}
        </Wrapper>
    );
};

export default SearchTokens;

const Wrapper = styled.div`
    position: relative;
    flex: 1 1 240px;
    max-width: 320px;
`;

const Tokens = styled.div`
    flex: 1;
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 4px 6px;
    min-width: 0;
`;

const Token = styled.span`
    display: flex;
    align-items: center;
    gap: 4px;
    background: ${({ $isGroup }) => ($isGroup ? "#E8EBFA" : "#F0F0FB")};
    color: #444791;
    font-size: 12px;
    font-weight: 600;
    padding: 2px 4px 2px 8px;
    border-radius: 3px;
    white-space: nowrap;
`;

const TokenRemove = styled(UnstyledButton)`
    color: #444791;
    font-size: 13px;
    line-height: 1;
    padding: 0 2px;
`;

const ClearButton = styled(UnstyledButton)`
    color: #616161;
    font-size: 15px;
    line-height: 1;
    padding: 0 2px;
`;

const Input = styled.input`
    border: none;
    background: none;
    outline: none;
    font: inherit;
    font-size: 13px;
    flex: 1;
    min-width: 90px;
    height: 22px;
`;

const GroupName = styled.span`
    font-weight: 600;
    flex: 1;
`;

const UserInfos = styled.span`
    display: flex;
    flex-direction: column;
    small {
        font-size: 11px;
        color: #616161;
    }
`;
